import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ProgressIndicator } from '@/components/onboarding/ProgressIndicator';

interface OnboardingLayoutProps {
  children: ReactNode;
  currentStep: number;
  totalSteps: number;
  onBack?: () => void;
  showProgressIndicator?: boolean;
  showBackButton?: boolean;
  title?: string;
  subtitle?: string;
  preventSkipping?: boolean;
}

export function OnboardingLayout({
  children,
  currentStep,
  totalSteps,
  onBack,
  showProgressIndicator = true,
  showBackButton = false,
  title,
  subtitle,
  preventSkipping: _preventSkipping = true,
}: OnboardingLayoutProps) {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen bg-linen flex flex-col">
      {/* Onboarding Header */}
      <header className="bg-white border-b border-lilac-900 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img src="/logo.png" alt="CareCal" className="h-8 w-auto" />
          </div>

          {/* Progress indicator */}
          {showProgressIndicator && (
            <ProgressIndicator currentStep={currentStep} totalSteps={totalSteps} />
          )}
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col">
        <div className="max-w-2xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
          {/* Back button - positioned at top level, outside card */}
          {showBackButton && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="flex items-center mb-6"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </Button>
          )}

          {/* Title and subtitle */}
          {(title || subtitle) && (
            <div className="mb-8 text-center">
              {title && (
                <h1 className="font-display text-3xl sm:text-4xl font-bold text-night-900 mb-3">
                  {title}
                </h1>
              )}
              {subtitle && (
                <p className="font-body text-lg text-night-500 max-w-lg mx-auto">{subtitle}</p>
              )}
            </div>
          )}

          {/* Form content */}
          <div className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-lilac-900 p-6 sm:p-8">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}
